import { KeyLearning } from '../../bass/BassEditor';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Circle, Trash2 } from 'lucide-react';
import { getAudioContext, getMasterGain } from '@/audio/AudioEngine';
import { MonoVoice, playNote } from '@/audio/instrumentVoices';
import { foldPitchToKey, KEY_OPTIONS, parseSessionKey, pitchInKey } from '@/audio/musicTheory';
import { MUSICAL_KEY_OFFSETS, quantizeNotes, shiftTypingOctave, SNAP_BEATS, snapBeat } from '@/audio/pianoRoll';
import { sanitizePattern } from '@/audio/patternModel';
import { findSound, getOneShotCategories, getOneShots, getSoundBuffer, loadProducerSounds } from '@/audio/producerSounds';
import { samplerDefaultsForSound, samplerPhraseOutlastsBuffer } from '@/audio/sampler';
import { ChordStarterPanel, chordStarterSummary } from './ChordStarter';

const ROW_HEIGHT = 12;
const MIN_BEAT_WIDTH = 44;
const KEY_WIDTH = 58;
const MAX_PITCH = 108;
const MIN_PITCH = 36;
const noteName = (pitch) => `${['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'][pitch % 12]}${Math.floor(pitch / 12) - 2}`;
const nativeControl = (target) => ['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA'].includes(target?.tagName) || target?.isContentEditable;

export function pianoToolbarLayout(viewportWidth) {
  const available = Math.max(0, Number(viewportWidth) - 330);
  const required = 650;
  return { available, required, overflow: available < required, rows: available < required ? 2 : 1 };
}

export function guardPianoRollKey(event, handlers = {}) {
  if (event.metaKey || event.ctrlKey || event.altKey || nativeControl(event.target)) return false;
  const key = String(event.key || '').toLowerCase();
  const musical = Boolean(handlers.noteOn) && Object.prototype.hasOwnProperty.call(MUSICAL_KEY_OFFSETS, key);
  const octave = Boolean(handlers.octave) && (key === 'z' || key === 'x');
  const handled = musical || octave || key === 'delete' || key === 'backspace'
    || event.code === 'Space' || event.key === ' ' || event.key === 'Spacebar';
  if (!handled) return false;
  event.preventDefault(); event.stopPropagation();
  if (musical) handlers.noteOn?.(key, event);
  else if (key === 'z' || key === 'x') handlers.octave?.(key);
  else if (key === 'delete' || key === 'backspace') handlers.delete?.();
  else if (event.code === 'Space' || event.key === ' ' || event.key === 'Spacebar') handlers.play?.();
  return true;
}

export function pixelsPerBeat(availableWidth, totalBeats, minimum = MIN_BEAT_WIDTH) {
  const width = Number(availableWidth);
  const beats = Number(totalBeats);
  return Math.max(minimum, width > 0 && beats > 0 ? width / beats : minimum);
}

function pointerPosition(event, element, patternBars, beatWidth, snap, parsedKey, lockToKey) {
  const rect = element.getBoundingClientRect();
  const totalBeats = patternBars * 4;
  const rawBeat = Math.max(0, Math.min(totalBeats - 1 / 64, (event.clientX - rect.left) / beatWidth));
  const snapped = snapBeat(rawBeat, snap);
  const minimumLength = snap === 'Off' ? 1 / 64 : SNAP_BEATS[snap];
  const rawPitch = MAX_PITCH - Math.floor((event.clientY - rect.top) / ROW_HEIGHT);
  return {
    beat: Math.max(0, Math.min(totalBeats - minimumLength, snapped)),
    pitch: lockToKey ? foldPitchToKey(rawPitch, parsedKey) : Math.max(MIN_PITCH, Math.min(MAX_PITCH, rawPitch)),
  };
}

export function PianoRoll({ clip, tracks = [], pattern: incomingPattern, bpm, keySignature, headerHost, onKeySignatureChange, onCommit, onPlayToggle, onStopArrangement, recording = false, liveOctave, onOctaveChange, onAuditionNoteOn, onAuditionNoteOff, onWriteMelody, onWriteBass }) {
  const isSampler = clip.patternType === 'sampler';
  const [draft, setDraft] = useState(() => sanitizePattern(clip.patternType, incomingPattern));
  const [snap, setSnap] = useState('1/16');
  const [strength, setStrength] = useState(100);
  const [quantizeLengths, setQuantizeLengths] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [lastLength, setLastLength] = useState(0.25);
  const [chordStarterOpen, setChordStarterOpen] = useState(false);
  const [starterSummary, setStarterSummary] = useState(() => chordStarterSummary(incomingPattern));
  const [soundOpen, setSoundOpen] = useState(false);
  const [chordLabelMode, setChordLabelMode] = useState('name');
  const [localOctave, setLocalOctave] = useState(3);
  const typingOctave = Number.isFinite(liveOctave) ? liveOctave : localOctave;
  const setTypingOctave = (update) => {
    const next = typeof update === 'function' ? update(typingOctave) : update;
    if (onOctaveChange) onOctaveChange(next); else setLocalOctave(next);
  };
  const [availableWidth, setAvailableWidth] = useState(0);
  const [, setSoundBankRevision] = useState(0);
  const [sampleBuffer, setSampleBuffer] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleCategory, setSampleCategory] = useState('');
  const [box, setBox] = useState(null);
  const draftRef = useRef(draft);
  const committedRef = useRef(JSON.stringify(draft));
  const dragRef = useRef(null);
  const scrollRef = useRef(null);
  const gridRef = useRef(null);
  const activeAuditions = useRef(new Map());
  const chordPreviewHandles = useRef([]);
  const chordPreviewTimer = useRef(null);
  const monoVoiceRef = useRef(null);
  const longPressRef = useRef(null);
  const idCounter = useRef(0);
  const parsedKey = useMemo(() => parseSessionKey(keySignature), [keySignature]);
  const lockToKey = draft.lockToKey;
  const totalBeats = draft.patternBars * 4;
  const beatWidth = pixelsPerBeat(availableWidth, totalBeats);
  const categories = getOneShotCategories();
  const selectedSound = isSampler ? findSound(draft.sampleId) : null;
  const activeCategory = sampleCategory || selectedSound?.category || categories[0]?.id || '';
  const categorySounds = isSampler ? getOneShots(activeCategory) : [];
  const hasSamples = isSampler && getOneShots().length > 0;

  useEffect(() => {
    const next = sanitizePattern(clip.patternType, incomingPattern);
    draftRef.current = next; committedRef.current = JSON.stringify(next); setDraft(next);
  }, [clip.patternType, incomingPattern]);
  useEffect(() => { setSampleCategory(''); }, [clip.id]);
  useEffect(() => {
    if (!isSampler) return undefined;
    let live = true;
    loadProducerSounds().then(() => { if (live) setSoundBankRevision((value) => value + 1); });
    return () => { live = false; };
  }, [isSampler]);
  useEffect(() => {
    if (!isSampler || !draft.sampleId) { setSampleBuffer(null); setSampleLoading(false); return undefined; }
    let live = true; setSampleLoading(true);
    getSoundBuffer(draft.sampleId).then((buffer) => { if (live) setSampleBuffer(buffer || null); })
      .catch(() => { if (live) setSampleBuffer(null); })
      .finally(() => { if (live) setSampleLoading(false); });
    return () => { live = false; };
  }, [draft.sampleId, isSampler]);
  useEffect(() => {
    monoVoiceRef.current?.stop(); monoVoiceRef.current = null;
  }, [draft.sampleId, draft.voice, sampleBuffer]);
  useEffect(() => {
    const viewport = scrollRef.current;
    if (viewport) viewport.scrollTop = (MAX_PITCH - 72) * ROW_HEIGHT;
  }, []);
  useEffect(() => {
    const viewport = scrollRef.current;
    if (!viewport) return undefined;
    const measure = () => setAvailableWidth(Math.max(0, viewport.clientWidth - KEY_WIDTH));
    measure();
    if (typeof ResizeObserver === 'undefined') { window.addEventListener('resize', measure); return () => window.removeEventListener('resize', measure); }
    const observer = new ResizeObserver(measure); observer.observe(viewport); return () => observer.disconnect();
  }, []);

  const updateDraft = useCallback((mutate) => {
    const next = sanitizePattern(clip.patternType, mutate(structuredClone(draftRef.current)));
    draftRef.current = next; setDraft(next); return next;
  }, [clip.patternType]);
  const commit = useCallback((next = draftRef.current) => {
    const signature = JSON.stringify(next);
    if (signature === committedRef.current) return false;
    committedRef.current = signature; onCommit(next); return true;
  }, [onCommit]);

  const currentVoiceSpec = useCallback(() => isSampler
    ? { ...draftRef.current, buffer: sampleBuffer }
    : draftRef.current, [isSampler, sampleBuffer]);

  const auditionOn = useCallback((pitch, velocity = 0.8) => {
    if (onAuditionNoteOn) { onAuditionNoteOn(pitch, velocity, { token: `editor-${pitch}` }); return; }
    const ctx = getAudioContext();
    const voiceSpec = currentVoiceSpec();
    if (draftRef.current.mono) {
      if (!monoVoiceRef.current && (!isSampler || voiceSpec.buffer)) monoVoiceRef.current = new MonoVoice(ctx, getMasterGain(), voiceSpec);
      monoVoiceRef.current?.noteOn(pitch, velocity, ctx.currentTime);
    } else {
      activeAuditions.current.get(pitch)?.stop?.(ctx.currentTime);
      const handle = playNote(ctx, getMasterGain(), voiceSpec, { pitch, velocity, when: ctx.currentTime, duration: 30 });
      if (handle) activeAuditions.current.set(pitch, handle);
    }
  }, [currentVoiceSpec, isSampler, onAuditionNoteOn]);
  const auditionOff = useCallback((pitch, at = null) => {
    if (onAuditionNoteOff) { onAuditionNoteOff(pitch, { token: `editor-${pitch}` }); return; }
    const ctx = getAudioContext(); const when = at ?? ctx.currentTime;
    if (draftRef.current.mono) monoVoiceRef.current?.noteOff(pitch, when);
    else { activeAuditions.current.get(pitch)?.stop?.(when); activeAuditions.current.delete(pitch); }
  }, [onAuditionNoteOff]);

  useEffect(() => {
    const blur = () => {
      monoVoiceRef.current?.stop();
      for (const handle of activeAuditions.current.values()) handle?.stop?.();
      activeAuditions.current.clear();
    };
    window.addEventListener('blur', blur);
    return () => { window.removeEventListener('blur', blur); };
  }, []);
  useEffect(() => () => {
    clearTimeout(longPressRef.current);
    clearTimeout(chordPreviewTimer.current);
    monoVoiceRef.current?.stop();
    for (const handle of activeAuditions.current.values()) handle?.stop?.();
    for (const handle of chordPreviewHandles.current) handle?.stop?.();
  }, []);

  const deleteSelected = useCallback(() => {
    if (!selected.size) return;
    const next = updateDraft((value) => ({ ...value, notes: value.notes.filter((note) => !selected.has(note.id)) }));
    setSelected(new Set()); commit(next);
  }, [commit, selected, updateDraft]);

  const deleteNote = useCallback((noteId) => {
    const next = updateDraft((value) => ({ ...value, notes: value.notes.filter((note) => note.id !== noteId) }));
    setSelected((ids) => { const nextIds = new Set(ids); nextIds.delete(noteId); return nextIds; }); commit(next);
  }, [commit, updateDraft]);

  const beginGrid = (event) => {
    if (event.button !== 0 || !gridRef.current) return;
    event.preventDefault(); gridRef.current.focus();
    const point = pointerPosition(event, gridRef.current, draft.patternBars, beatWidth, snap, parsedKey, lockToKey);
    if (event.shiftKey && event.pointerType !== 'touch') {
      dragRef.current = { type: 'box', x: event.clientX, y: event.clientY };
      setBox({ left: event.clientX, top: event.clientY, width: 0, height: 0 }); return;
    }
    const length = snap === 'Off' ? lastLength : SNAP_BEATS[snap];
    const note = { id: `note-${Date.now()}-${idCounter.current += 1}`, start: point.beat, length: Math.min(length, totalBeats - point.beat), pitch: point.pitch, velocity: 0.8 };
    const next = updateDraft((value) => ({ ...value, notes: [...value.notes, note] }));
    setSelected(new Set([note.id])); setLastLength(note.length);
    dragRef.current = { type: 'new', noteId: note.id, startX: event.clientX, startY: event.clientY, original: next.notes };
  };

  const beginNoteDrag = (event, note, resize = false) => {
    if (event.button !== 0) return;
    event.preventDefault(); event.stopPropagation(); gridRef.current?.focus();
    const ids = selected.has(note.id) ? new Set(selected) : new Set([note.id]); setSelected(ids);
    dragRef.current = { type: resize ? 'resize' : 'move', noteId: note.id, startX: event.clientX, startY: event.clientY, original: structuredClone(draftRef.current.notes), ids };
    if (event.pointerType === 'touch') longPressRef.current = setTimeout(() => { dragRef.current = null; deleteNote(note.id); }, 500);
  };

  useEffect(() => {
    const move = (event) => {
      const drag = dragRef.current; if (!drag) return;
      if (Math.abs(event.clientX - drag.startX) > 5 || Math.abs(event.clientY - drag.startY) > 5) clearTimeout(longPressRef.current);
      if (drag.type === 'box') {
        setBox({ left: Math.min(drag.x, event.clientX), top: Math.min(drag.y, event.clientY), width: Math.abs(event.clientX - drag.x), height: Math.abs(event.clientY - drag.y) });
        return;
      }
      if (drag.type === 'new') return;
      const beatDeltaRaw = (event.clientX - drag.startX) / beatWidth;
      const beatDelta = snap === 'Off' ? beatDeltaRaw : snapBeat(beatDeltaRaw, snap);
      const pitchDelta = -Math.round((event.clientY - drag.startY) / ROW_HEIGHT);
      updateDraft((value) => ({ ...value, notes: drag.original.map((note) => {
        if (!drag.ids.has(note.id)) return note;
        if (drag.type === 'resize') {
          const minimum = snap === 'Off' ? 1 / 64 : SNAP_BEATS[snap];
          return { ...note, length: Math.max(minimum, Math.min(totalBeats - note.start, note.length + beatDelta)) };
        }
        const start = Math.max(0, Math.min(totalBeats - note.length, note.start + beatDelta));
        const rawPitch = Math.max(MIN_PITCH, Math.min(MAX_PITCH, note.pitch + pitchDelta));
        return { ...note, start, pitch: lockToKey && parsedKey ? foldPitchToKey(rawPitch, parsedKey) : rawPitch };
      }) }));
    };
    const up = (event) => {
      clearTimeout(longPressRef.current);
      const drag = dragRef.current; if (!drag) return; dragRef.current = null;
      if (drag.type === 'box') {
        const rect = gridRef.current?.getBoundingClientRect();
        if (rect) {
          const left = Math.min(drag.x, event.clientX) - rect.left; const right = Math.max(drag.x, event.clientX) - rect.left;
          const top = Math.min(drag.y, event.clientY) - rect.top; const bottom = Math.max(drag.y, event.clientY) - rect.top;
          setSelected(new Set(draftRef.current.notes.filter((note) => {
            const x = note.start * beatWidth; const y = (MAX_PITCH - note.pitch) * ROW_HEIGHT;
            return x + note.length * beatWidth >= left && x <= right && y + ROW_HEIGHT >= top && y <= bottom;
          }).map((note) => note.id)));
        }
        setBox(null); return;
      }
      commit();
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up); };
  }, [beatWidth, commit, lockToKey, parsedKey, snap, totalBeats, updateDraft]);

  const removeNote = (event, noteId) => {
    event.preventDefault(); event.stopPropagation();
    deleteNote(noteId);
  };

  const changeVoice = (voice) => {
    const next = updateDraft((value) => ({ ...value, voice, mono: voice === '808' ? true : value.mono, glide: voice === '808' && !value.glide ? 80 : value.glide })); commit(next);
  };
  const control = (field, value) => commit(updateDraft((patternValue) => ({ ...patternValue, [field]: value })));
  const doQuantize = () => commit(updateDraft((value) => ({ ...value, notes: quantizeNotes(value.notes, { snap, strength, lengths: quantizeLengths, selectedIds: selected }) })));

  const writeChords = (nextPattern) => {
    draftRef.current = nextPattern; setDraft(nextPattern); setStarterSummary(chordStarterSummary(nextPattern)); commit(nextPattern);
  };
  const clearChordLabels = () => {
    const next = updateDraft((value) => ({ ...value, chords: [], chordSource: null })); commit(next);
  };
  const previewChords = (notes, patternBars) => {
    if (chordPreviewHandles.current.length || chordPreviewTimer.current) {
      clearTimeout(chordPreviewTimer.current); chordPreviewTimer.current = null;
      chordPreviewHandles.current.forEach((handle) => handle?.stop?.()); chordPreviewHandles.current = [];
      return false;
    }
    const ctx = getAudioContext();
    const beatSeconds = 60 / Math.max(1, Number(bpm) || 120);
    const voiceSpec = currentVoiceSpec();
    chordPreviewHandles.current = notes.map((note) => playNote(ctx, getMasterGain(), voiceSpec, {
      pitch: note.pitch, velocity: note.velocity, when: ctx.currentTime + note.start * beatSeconds, duration: note.length * beatSeconds,
    })).filter(Boolean);
    chordPreviewTimer.current = setTimeout(() => { chordPreviewHandles.current = []; chordPreviewTimer.current = null; }, patternBars * 4 * beatSeconds * 1000 + 50);
    return true;
  };

  const chooseSample = async (sound) => {
    if (!sound) return;
    const defaults = samplerDefaultsForSound(sound);
    const next = updateDraft((value) => ({ ...value, ...defaults }));
    commit(next); setSampleCategory(sound.category || '');
    try {
      const buffer = await getSoundBuffer(sound.id);
      if (!buffer) return;
      setSampleBuffer(buffer);
      const ctx = getAudioContext(); if (ctx.state === 'suspended') await ctx.resume();
      playNote(ctx, getMasterGain(), { ...next, gate: false, buffer }, { pitch: next.rootNote, velocity: 0.85, when: ctx.currentTime, duration: 30 });
    } catch (error) { console.warn('Sampler audition failed:', error.message); }
  };

  const pitches = Array.from({ length: MAX_PITCH - MIN_PITCH + 1 }, (_, index) => MAX_PITCH - index);
  const gridWidth = totalBeats * beatWidth;
  const playheadBeat = Math.max(0, (((clip.playheadBar ?? 0) - clip.start_bar) * 4 + Number(clip.offset || 0) * 4) % totalBeats);
  const sampleMissing = isSampler && !selectedSound;
  const phraseOutlastsSample = isSampler && samplerPhraseOutlastsBuffer(draft, sampleBuffer, bpm);
  const headerControls = <div className="pattern-header-actions" data-testid="pattern-recording-controls">
    <button className={chordStarterOpen ? 'active' : ''} onClick={() => setChordStarterOpen((open) => { if (open) setStarterSummary(chordStarterSummary(draftRef.current)); return !open; })}>Chord Starter</button>
  </div>;
  return <div className="piano-editor">
    {headerHost && createPortal(headerControls, headerHost)}
    <div className="piano-toolbar">
      <label>Snap <select title="Piano roll snap" value={snap} onChange={(event) => setSnap(event.target.value)}>{Object.keys(SNAP_BEATS).map((value) => <option key={value}>{value}</option>)}</select></label>
      <label>Strength <select title="Quantize strength" value={strength} onChange={(event) => setStrength(Number(event.target.value))}>{[100, 75, 50].map((value) => <option key={value} value={value}>{value}%</option>)}</select></label>
      <label><input title="Quantize note lengths too" type="checkbox" checked={quantizeLengths} onChange={(event) => setQuantizeLengths(event.target.checked)} /> Lengths too</label>
      <button title="Quantize selected notes, or every note when none are selected" onClick={doQuantize}>Quantize</button>
      <label><input type="checkbox" checked={lockToKey && Boolean(parsedKey)} disabled={!parsedKey} onChange={(event) => control('lockToKey', event.target.checked)} title={parsedKey ? 'Keep notes in the song key' : 'Choose a valid song key to use key lock'} /> Lock to key</label>
      <label>Key <select aria-label="Session key" title="Song key" value={keySignature || ''} onChange={(event) => onKeySignatureChange?.(event.target.value || null)}>{KEY_OPTIONS.map((value) => <option key={value || 'none'} value={value}>{value || 'No key'}</option>)}</select></label>
      <button title="Typing octave down (Z)" onClick={() => setTypingOctave((value) => shiftTypingOctave(value, 'z'))}>Oct -</button>
      <span>C{typingOctave}</span><button title="Typing octave up (X)" onClick={() => setTypingOctave((value) => shiftTypingOctave(value, 'x'))}>Oct +</button>
      <button data-testid="sound-popover-button" aria-expanded={soundOpen} onClick={() => setSoundOpen((open) => !open)}>Sound</button>
      <button title="Delete selected notes" disabled={!selected.size} onClick={deleteSelected}><Trash2 size={13} /></button>
    </div>
    {soundOpen && <div className="piano-sound-popover" data-testid="sound-popover">
      {!isSampler && <label>Voice <select title="Choose a keys sound" value={draft.voice} onChange={(event) => changeVoice(event.target.value)}>{['keys', 'strings', '808', 'fingerbass', 'pluck'].map((voice) => <option key={voice} value={voice}>{voice === 'fingerbass' ? 'Finger bass' : voice}</option>)}</select></label>}
      {isSampler && <><label>Root <input aria-label="Sampler root note" title="Root note as a MIDI number" type="number" min="0" max="127" value={draft.rootNote} onChange={(event) => control('rootNote', Number(event.target.value))} /></label><label>Tune <input aria-label="Sampler tune" type="number" min="-12" max="12" value={draft.tune} onChange={(event) => control('tune', Number(event.target.value))} /></label><label>Fine <input aria-label="Sampler fine tune" type="number" min="-50" max="50" value={draft.fine} onChange={(event) => control('fine', Number(event.target.value))} /></label><label><input title="Stop the sample at the note end" type="checkbox" checked={draft.gate} onChange={(event) => control('gate', event.target.checked)} /> Cut at note end</label></>}
      <label><input title="Play one note at a time" type="checkbox" checked={draft.mono} onChange={(event) => control('mono', event.target.checked)} /> Mono</label>
      <label>Glide <input title="Pitch glide in milliseconds" type="range" min="0" max="500" value={draft.glide} onChange={(event) => updateDraft((value) => ({ ...value, glide: Number(event.target.value) }))} onPointerUp={() => commit()} onBlur={() => commit()} /></label>
      <label>Reverb <input title="Reverb amount" type="range" min="0" max="1" step="0.01" value={draft.reverb} onChange={(event) => updateDraft((value) => ({ ...value, reverb: Number(event.target.value) }))} onPointerUp={() => commit()} onBlur={() => commit()} /></label>
      <label>Echo <input title="Echo amount" type="range" min="0" max="1" step="0.01" value={draft.echo} onChange={(event) => updateDraft((value) => ({ ...value, echo: Number(event.target.value) }))} onPointerUp={() => commit()} onBlur={() => commit()} /></label>
      <label>Attack <input title="Attack in seconds" type="range" min="0.001" max="0.5" step="0.001" value={draft.attack} onChange={(event) => updateDraft((value) => ({ ...value, attack: Number(event.target.value) }))} onPointerUp={() => commit()} onBlur={() => commit()} /></label>
      <label>Release <input title="Release in seconds" type="range" min="0.01" max="2" step="0.01" value={draft.release} onChange={(event) => updateDraft((value) => ({ ...value, release: Number(event.target.value) }))} onPointerUp={() => commit()} onBlur={() => commit()} /></label>
    </div>}
    {chordStarterOpen ? <div className="starter-takeover" data-testid="starter-takeover"><ChordStarterPanel clip={clip} tracks={tracks} patternType={clip.patternType} pattern={draft} bpm={bpm} keySignature={keySignature}
      onKeySignatureChange={onKeySignatureChange} onWrite={writeChords} onPreview={previewChords} onClearLabels={clearChordLabels} onWriteMelody={onWriteMelody} onWriteBass={onWriteBass}
      onCollapse={(nextSummary) => { setStarterSummary(nextSummary || chordStarterSummary(draftRef.current)); setChordStarterOpen(false); }} /></div> : <>
    {starterSummary && <div className="chord-starter-summary" data-testid="chord-starter-summary"><span>{starterSummary}</span><button onClick={() => setChordStarterOpen(true)}>Change</button></div>}
    {isSampler && <div className="sampler-browser" data-testid="sampler-browser">
      <div className="sampler-categories" role="tablist" aria-label="Sample categories">{categories.map((category) => <button key={category.id} role="tab" aria-selected={activeCategory === category.id} className={activeCategory === category.id ? 'active' : ''} onClick={() => setSampleCategory(category.id)}>{category.label}</button>)}</div>
      <div className="sampler-sounds">{sampleMissing && <strong data-testid="sampler-sound-missing">Sound missing</strong>}{sampleLoading && <span>Loading sound...</span>}{!hasSamples && <span>No sounds loaded yet</span>}{hasSamples && !categorySounds.length && <span>No sounds in this category</span>}{categorySounds.map((sound) => <button key={sound.id} className={draft.sampleId === sound.id ? 'active' : ''} title={`Load and hear ${sound.name}`} onClick={() => chooseSample(sound)}>{sound.name}</button>)}</div>
    </div>}
    {phraseOutlastsSample && <div className="sampler-phrase-hint" data-testid="sampler-phrase-hint">This mono phrase is longer than the sample at its root note, so it goes silent before the phrase ends.</div>}
    <KeyLearning keySignature={keySignature} getContext={getAudioContext} destination={getMasterGain} onStopArrangement={onStopArrangement} />
    <div className="piano-legend"><Circle size={8} /> A W S E D F T G Y H U J K plays notes. Z and X change octave. Shift-drag selects.</div>
    <div className="piano-scroll" ref={scrollRef}>
      {draft.chords.length > 0 && <div className="chord-label-lane" data-testid="chord-label-lane" style={{ width: gridWidth + KEY_WIDTH }}>
        <button title="Switch chord label names and roman numerals" onClick={() => setChordLabelMode((mode) => mode === 'name' ? 'roman' : 'name')}>{chordLabelMode === 'name' ? 'Names' : 'Roman'}</button>
        <div className="chord-labels" style={{ width: gridWidth }}>{Array.from({ length: draft.patternBars }, (_, bar) => {
          const chord = draft.chords.find((item) => item.bar === bar);
          return <span key={bar} style={{ width: beatWidth * 4 }}>{chord ? chordLabelMode === 'name' ? chord.symbol : chord.roman : ''}</span>;
        })}</div>
      </div>}
      <div className="piano-layout" style={{ width: gridWidth + KEY_WIDTH, height: pitches.length * ROW_HEIGHT }}>
        <div className="piano-keys">{pitches.map((pitch) => <button key={pitch} title={`Play ${noteName(pitch)}${isSampler && pitch === draft.rootNote ? ', sampler root note' : ''}`} className={`${pitch % 12 === 0 ? 'c-note' : ''}${[1, 3, 6, 8, 10].includes(pitch % 12) ? ' black' : ''}${isSampler && pitch === draft.rootNote ? ' sampler-root' : ''}`} onPointerDown={() => auditionOn(lockToKey && parsedKey ? foldPitchToKey(pitch, parsedKey) : pitch)} onPointerUp={() => auditionOff(lockToKey && parsedKey ? foldPitchToKey(pitch, parsedKey) : pitch)}>{isSampler && pitch === draft.rootNote ? `Root ${noteName(pitch)}` : pitch % 12 === 0 ? noteName(pitch) : ''}</button>)}</div>
        <div data-testid="piano-roll-surface" className="piano-grid" ref={gridRef} tabIndex={0} style={{ width: gridWidth, height: pitches.length * ROW_HEIGHT }}
          onPointerDown={beginGrid} onKeyDown={(event) => guardPianoRollKey(event, { delete: deleteSelected, play: onPlayToggle })}>
          {pitches.map((pitch, index) => <div key={pitch} className={`piano-row${[1, 3, 6, 8, 10].includes(pitch % 12) ? ' black' : ''}${lockToKey && parsedKey && !pitchInKey(pitch, parsedKey) ? ' out-key' : ''}`} style={{ top: index * ROW_HEIGHT }} />)}
          {Array.from({ length: totalBeats * 4 + 1 }, (_, index) => <i key={index} className={`piano-line${index % 16 === 0 ? ' bar' : index % 4 === 0 ? ' beat' : ''}`} style={{ left: index * beatWidth / 4 }} />)}
          {draft.notes.map((note) => <div key={note.id} data-note-id={note.id} className={`piano-note${selected.has(note.id) ? ' selected' : ''}`} style={{ left: note.start * beatWidth, top: (MAX_PITCH - note.pitch) * ROW_HEIGHT + 1, width: Math.max(5, note.length * beatWidth), opacity: 0.35 + note.velocity * 0.65 }}
            title={`${noteName(note.pitch)}, ${note.length.toFixed(2)} beats`} onPointerDown={(event) => beginNoteDrag(event, note)} onContextMenu={(event) => removeNote(event, note.id)} onDoubleClick={(event) => removeNote(event, note.id)}>
            <span className="piano-note-resize" onPointerDown={(event) => beginNoteDrag(event, note, true)} />
          </div>)}
          {box && <div className="piano-box" style={{ left: box.left - (gridRef.current?.getBoundingClientRect().left || 0), top: box.top - (gridRef.current?.getBoundingClientRect().top || 0), width: box.width, height: box.height }} />}
          {clip.isPlaying && <div className="piano-playhead" style={{ left: playheadBeat * beatWidth }} />}
        </div>
      </div>
    </div>
    </>}
  </div>;
}

export default PianoRoll;
